import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { BiblePassage, SetlistSong, WorshipService } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime, fromLocalInput, toLocalInput } from '../calendar/dates';
import { NoteBody, passageBlock } from '../devotional/noteMarkup';
import { PassagePicker } from '../devotional/PassagePicker';
import { useMe } from '../session/SessionProvider';
import { OfferingsCard, WelcomeCard } from './ServiceCards';
import { OrderCard } from './ServiceOrder';
import { BandSection, type Band } from './BandCard';
import { CafeteriaCard, type CafeteriaMenu } from './CafeteriaCard';
import { formatMoney } from '../finance/money';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { SetlistEditor } from '../songs/ServiceSongsPage';
import { useVisitorAccess } from '../visitors/shared';
import { useShowRehearsals } from './RehearsalPages';

type Ref = { personId: string; name: string };
type Material = { id: string; name: string; kind: 'image' | 'audio' | 'video' | 'document'; mime: string; pages: number | null };
type ProgramSong = { id: string; position: number; title: string; author: string | null; key: string | null; version: string | null; notes: string | null; lyrics: string; links: string[] };
export type Program = {
  service: WorshipService;
  preacher: Ref | null;
  theme: string | null;
  passages: string[];
  scripture: string | null;
  pastorNotes: string | null;
  worshipNotes: string | null;
  songs: ProgramSong[];
  canEditPlan: boolean;
  /** Material de apoyo de la Predicación (en orden). Lo suben quien predica o la administración. */
  materials?: Material[];
  canEditMaterials?: boolean;
  canEditSongs: boolean;
  canEditWorshipNotes: boolean;
  /** Puestos de Alabanza en este culto (Batería, Bajo…). */
  band?: Band;
};

/** Programa del culto: predicación, cantos con letra y notas. Lo ven todos; cada quien edita lo suyo. */
export function ServiceProgramPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const { serviceId = '' } = useParams();
  const [params] = useSearchParams();
  const base = `/worship/churches/${churchId}/services/${serviceId}`;
  const data = useAsync((signal) => (churchId ? api.get<Program>(`${base}/program`, signal) : Promise.resolve(null)), [base]);
  const cafeteria = useAsync((signal) => (churchId ? api.get<CafeteriaMenu>(`${base}/cafeteria`, signal).catch(() => null) : Promise.resolve(null)), [base]);
  // Bienvenida y ofrendas: la pestaña solo aparece para quien las atiende (y la administración).
  const svc = `/churches/${churchId}/services/${serviceId}`;
  const door = useAsync((signal) => (churchId ? api.get<DoorSummary>(`/assignments${svc}/door`, signal).catch(() => null) : Promise.resolve(null)), [svc]);
  const offerings = useAsync((signal) => (churchId ? api.get<OfferingsSummary>(`/finance${svc}/offerings`, signal).catch(() => null) : Promise.resolve(null)), [svc]);
  const visitors = useVisitorAccess(churchId);
  const showRehearsals = useShowRehearsals(churchId);
  const [editing, setEditing] = useState(false);
  const [songsEditing, setSongsEditing] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [opening, setOpening] = useState(false);
  const navigate = useNavigate();
  async function openForum() {
    setOpening(true);
    try {
      const { id } = await api.post<{ id: string }>(`/communication/churches/${churchId}/forum/services/${serviceId}`);
      navigate(`/app/foro/${id}`);
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      setOpening(false);
    }
  }
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const p = data.data;
  if (!p) return null;
  const s = p.service;
  const canceled = s.status === 'canceled';

  const menu = cafeteria.status === 'ready' ? cafeteria.data : null;
  const d = door.status === 'ready' ? door.data : null;
  const f = offerings.status === 'ready' ? offerings.data : null;
  const welcome = Boolean(d?.visible || f?.canCapture || visitors.canUse);
  // Cafetería: la ve la iglesia cuando abre en este culto; su ministerio, siempre (para abrirla).
  const tabs = TABS.filter((t) => (t.key !== 'cafeteria' || (menu && (menu.open || menu.canEdit))) && (t.key !== 'bienvenida' || welcome));
  const team = me.can('assignments.read') && !canceled;
  const attendance = me.can('attendance.record') && !canceled;
  const tab: Tab = tabs.some((t) => t.key === params.get('ver')) ? (params.get('ver') as Tab) : 'resumen';
  const tabLink = (t: Tab) => (t === 'resumen' ? `/app/cultos/${serviceId}` : `/app/cultos/${serviceId}?ver=${t}`);

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/cultos">← Cultos</Link>
      </p>
      <header className="page__header page__header--actions">
        <div>
          <h1>{s.title}</h1>
          <p className="muted">
            {formatDayHeading(new Date(s.startsAt))} · {formatTime(s.startsAt)} a {formatTime(s.endsAt)}
            {s.location ? ` · ${s.location}` : ''}
            {canceled ? ' · Cancelado' : ''}
          </p>
        </div>
        <div className="actions actions--wrap">
          <Button variant="secondary" busy={opening} onClick={() => void openForum()}>
            Comentar el culto
          </Button>
          {team ? (
            <Link className="btn btn--secondary" to={`/app/cultos/${s.id}/equipo`}>
              Equipo
            </Link>
          ) : null}
          {attendance ? (
            <Link className="btn btn--secondary" to={`/app/cultos/${s.id}/asistencia`}>
              Asistencia
            </Link>
          ) : null}
          <MoreActions churchId={churchId} service={s} manage={me.can('worship.manage') && s.status === 'planned'} onChange={data.reload} />
        </div>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}

      <nav className="tabs tabs--scroll" aria-label="Secciones del culto">
        {tabs.map((t) => (
          <Link key={t.key} to={tabLink(t.key)} replace className={`tabs__tab${tab === t.key ? ' tabs__tab--active' : ''}`} aria-current={tab === t.key ? 'page' : undefined}>
            {t.label}
          </Link>
        ))}
      </nav>

      {tab === 'resumen' ? <Summary churchId={churchId} serviceId={serviceId} program={p} tabLink={tabLink} cafeteria={menu} door={d} offerings={f} /> : null}

      {tab === 'cafeteria' && menu ? <CafeteriaCard base={base} menu={menu} onChange={cafeteria.reload} /> : null}

      {tab === 'orden' ? <OrderCard churchId={churchId} serviceId={serviceId} band={p.band} /> : null}

      {tab === 'predicacion' ? (
        <Card title="Predicación" action={p.canEditPlan && !editing ? <Button variant="ghost" onClick={() => setEditing(true)}>Editar</Button> : null}>
          {editing ? (
            <PlanForm
              churchId={churchId}
              base={base}
              program={p}
              onDone={(ok) => {
                setEditing(false);
                if (ok) {
                  setMsg({ tone: 'success', text: 'Predicación guardada. Si cambió quién predica, ya le avisamos.' });
                  data.reload();
                }
              }}
            />
          ) : (
            <>
              <dl className="details">
                <dt>Predica</dt>
                <dd>{p.preacher ? p.preacher.name : <span className="muted">Por definir</span>}</dd>
                <dt>Tema</dt>
                <dd>{p.theme ?? <span className="muted">Por definir</span>}</dd>
                {p.passages.length ? (
                  <>
                    <dt>Versículos</dt>
                    <dd>{p.passages.join(' · ')}</dd>
                  </>
                ) : null}
              </dl>
              {p.scripture ? <NoteBody text={p.scripture} /> : null}
              {p.pastorNotes ? (
                <p className="small">
                  <strong>Notas del pastor:</strong> <span className="pre-line">{p.pastorNotes}</span>
                </p>
              ) : null}
            </>
          )}
        </Card>
      ) : null}

      {tab === 'predicacion' ? <MaterialsCard churchId={churchId} serviceId={serviceId} materials={p.materials ?? []} canEdit={Boolean(p.canEditMaterials)} onChange={data.reload} /> : null}

      {tab === 'alabanza' && songsEditing ? (
        <ServiceSongsEditor
          churchId={churchId}
          serviceId={serviceId}
          onDone={(saved) => {
            setSongsEditing(false);
            if (saved) {
              setMsg({ tone: 'success', text: 'Cantos guardados. Multimedia ya los puede traer a la Presentación.' });
              data.reload();
            }
          }}
        />
      ) : null}

      {tab === 'alabanza' ? (
        <Card
          title="Alabanza"
          action={
            <span className="actions">
              {showRehearsals ? (
                <Link className="btn btn--ghost" to="/app/ensayos">
                  Ensayos
                </Link>
              ) : null}
              {p.canEditSongs && !songsEditing && !canceled ? (
                <Button variant="secondary" onClick={() => setSongsEditing(true)}>
                  {p.songs.length ? 'Cambiar cantos' : 'Elegir cantos'}
                </Button>
              ) : null}
            </span>
          }
        >
          {songsEditing ? null : (
            <>
              {p.songs.length === 0 ? <p className="muted">Alabanza todavía no elige los cantos.</p> : null}
              <ol className="program-songs">
                {p.songs.map((x) => (
                  <li key={`${x.id}-${x.position}`}>
                    <details>
                      <summary>
                        <strong>{x.title}</strong>
                        {x.notes ? <span className="badge">{x.notes}</span> : null}
                        <span className="small muted">
                          {x.key ? ` Tono ${x.key}` : ''}
                          {x.version ? ` · ${x.version}` : ''}
                          {x.author ? ` · ${x.author}` : ''}
                        </span>
                      </summary>
                      {x.lyrics ? <p className="pre-line program-songs__lyrics">{x.lyrics.replace(/^\[(.+)\]$/gm, '— $1 —')}</p> : <p className="muted small">Sin letra registrada.</p>}
                      {x.links.length ? (
                        <p className="small">
                          {x.links.map((l) => (
                            <a key={l} href={l} target="_blank" rel="noopener noreferrer" className="block">
                              {l}
                            </a>
                          ))}
                        </p>
                      ) : null}
                    </details>
                  </li>
                ))}
              </ol>
              {p.songs.length ? <p className="small muted">En Presentación, al elegir este culto, usa «Traer los cantos del culto» y las letras se cargan solas.</p> : null}
            </>
          )}
          {p.band ? <BandSection base={base} band={p.band} onSaved={data.reload} /> : null}
          <WorshipNotes base={base} notes={p.worshipNotes} canEdit={p.canEditWorshipNotes} onSaved={data.reload} />
        </Card>
      ) : null}

      {tab === 'bienvenida' ? (
        <>
          <WelcomeCard churchId={churchId} serviceId={serviceId} />
          <OfferingsCard churchId={churchId} serviceId={serviceId} />
        </>
      ) : null}
    </div>
  );
}

type Tab = 'resumen' | 'orden' | 'predicacion' | 'alabanza' | 'bienvenida' | 'cafeteria';
const TABS: { key: Tab; label: string }[] = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'orden', label: 'Orden' },
  { key: 'predicacion', label: 'Predicación' },
  { key: 'alabanza', label: 'Alabanza' },
  { key: 'bienvenida', label: 'Bienvenida y ofrendas' },
  { key: 'cafeteria', label: 'Cafetería' },
];

type OrderSummary = { items: { inCharge: string; ministry: unknown; person: unknown; sameAsWelcome: boolean }[] };
type DoorSummary = { visible: boolean; assigned: { name: string }[] };
type OfferingsSummary = { canCapture: boolean; items: unknown[]; totals: { total: number }; accounts: { currency: string }[] };

/** Resumen: cada parte del culto con su estado en una línea; tocarla abre su pestaña. */
function Summary({
  churchId,
  serviceId,
  program: p,
  tabLink,
  cafeteria,
  door: d,
  offerings: f,
}: {
  churchId: string;
  serviceId: string;
  program: Program;
  tabLink: (t: Tab) => string;
  cafeteria: CafeteriaMenu | null;
  door: DoorSummary | null;
  offerings: OfferingsSummary | null;
}) {
  const svc = `/churches/${churchId}/services/${serviceId}`;
  const order = useAsync((signal) => api.get<OrderSummary>(`/worship${svc}/order`, signal).catch(() => null), [svc]);

  const tiles: { tab: Tab; title: string; lines: string[] }[] = [];
  tiles.push({
    tab: 'predicacion',
    title: 'Predicación',
    lines: [`Predica: ${p.preacher?.name ?? 'por definir'}`, p.theme ? `Tema: ${p.theme}` : 'Tema por definir'],
  });
  const band = p.band?.positions ?? [];
  const missing = band.filter((x) => x.people.length === 0).length;
  tiles.push({
    tab: 'alabanza',
    title: 'Alabanza',
    lines: [
      p.songs.length ? `${p.songs.length} ${p.songs.length === 1 ? 'canto elegido' : 'cantos elegidos'}` : 'Sin cantos todavía',
      ...(band.length ? [missing ? `Equipo: ${missing === 1 ? 'falta 1 puesto' : `faltan ${missing} puestos`}` : 'Equipo completo'] : []),
    ],
  });
  const o = order.status === 'ready' ? order.data : null;
  if (o && o.items.length) {
    const ready = o.items.filter((i) => i.inCharge === 'no_aplica' || i.ministry || i.person || i.sameAsWelcome).length;
    tiles.push({ tab: 'orden', title: 'Orden del culto', lines: [`${ready} de ${o.items.length} partes con responsable`] });
  }
  const welcome: string[] = [];
  if (d?.visible) welcome.push(d.assigned.length ? `En la puerta: ${d.assigned.map((a) => a.name).join(', ')}` : 'Nadie asignado a la puerta');
  if (f?.canCapture) welcome.push(f.items.length ? `Ofrendas: ${formatMoney(f.totals.total, f.accounts[0]?.currency ?? 'MXN')}` : 'Ofrendas sin registrar');
  if (welcome.length) tiles.push({ tab: 'bienvenida', title: 'Bienvenida y ofrendas', lines: welcome });
  if (cafeteria?.open) {
    const n = cafeteria.items.filter((i) => !i.hidden && !i.soldOut).length;
    tiles.push({ tab: 'cafeteria', title: 'Cafetería', lines: [n ? `${n} ${n === 1 ? 'producto' : 'productos'} a la venta` : 'Menú por publicar'] });
  }

  return (
    <ul className="summary-tiles">
      {tiles.map((t) => (
        <li key={t.tab}>
          <Link className="summary-tile" to={tabLink(t.tab)} replace>
            <strong>{t.title}</strong>
            {t.lines.map((l) => (
              <span key={l} className="small">
                {l}
              </span>
            ))}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Elegir, ordenar y poner el tono de los cantos ahí mismo, en la pestaña Alabanza. */
function ServiceSongsEditor({ churchId, serviceId, onDone }: { churchId: string; serviceId: string; onDone: (saved: boolean) => void }) {
  const url = `/worship/churches/${churchId}/services/${serviceId}/songs`;
  const set = useAsync((signal) => api.get<{ items: SetlistSong[] }>(url, signal), [url]);
  if (set.status === 'loading') return <Loading />;
  if (set.status === 'error') return <ErrorState error={set.error} onRetry={set.reload} />;
  return (
    <SetlistEditor
      churchId={churchId}
      url={url}
      title="Cantos del culto"
      initial={set.data.items.map((x) => ({ songId: x.id, title: x.title, key: x.serviceKey ?? '', notes: x.serviceNotes ?? '' }))}
      onDone={onDone}
    />
  );
}

function WorshipNotes({ base, notes, canEdit, onSaved }: { base: string; notes: string | null; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!editing) {
    return (
      <div className="program-notes">
        {notes ? (
          <p className="small">
            <strong>Notas de Alabanza:</strong> <span className="pre-line">{notes}</span>
          </p>
        ) : null}
        {canEdit ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            {notes ? 'Editar notas de Alabanza' : 'Agregar notas de Alabanza'}
          </Button>
        ) : null}
      </div>
    );
  }
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.put(`${base}/worship-notes`, { notes: String(new FormData(e.currentTarget).get('notes') ?? '').trim() || null });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="form" onSubmit={save}>
      {error ? <Alert>{error}</Alert> : null}
      <div className="field">
        <label htmlFor="worship-notes">Notas de Alabanza (para el equipo y Multimedia)</label>
        <textarea id="worship-notes" name="notes" rows={3} maxLength={4000} defaultValue={notes ?? ''} placeholder="Ensayo, entradas, cambios de tono…" />
      </div>
      <div className="actions">
        <Button type="submit" busy={busy}>Guardar notas</Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
      </div>
    </form>
  );
}

function PlanForm({ churchId, base, program: p, onDone }: { churchId: string; base: string; program: Program; onDone: (ok: boolean) => void }) {
  const me = useMe();
  const people = useAsync(
    (signal) => api.get<{ items: { personId: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items).catch(() => []),
    [churchId],
  );
  const [scripture, setScripture] = useState(p.scripture ?? '');
  const [passages, setPassages] = useState<string[]>(p.passages);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  async function insert(ref: string) {
    setFetching(true);
    setError(null);
    try {
      const passage = await api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(ref)}`);
      const block = passageBlock(passage.label, passage.translation?.code ?? null, passage.verses);
      setScripture((t) => (t.trim() ? `${t.replace(/\s+$/, '')}\n\n${block}\n` : `${block}\n`));
      setPassages((l) => (l.includes(passage.label) ? l : [...l, passage.label]));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setFetching(false);
    }
  }
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    setBusy(true);
    setError(null);
    try {
      await api.put(`${base}/plan`, { preacherId: t('preacherId'), theme: t('theme'), passages, scripture: scripture.trim() || null, pastorNotes: t('pastorNotes') });
      onDone(true);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  const list = people.status === 'ready' ? people.data : [];
  const options = list.some((x) => x.personId === me.personId) ? list : [{ personId: me.personId, givenName: 'Yo', familyName: '' }, ...list];
  return (
    <form className="form" onSubmit={save}>
      {error ? <Alert>{error}</Alert> : null}
      <div className="field">
        <label htmlFor="plan-preacher">Quién predica</label>
        <SearchSelect
          id="plan-preacher"
          name="preacherId"
          defaultValue={p.preacher?.personId ?? ''}
          emptyLabel="Por definir"
          options={[
            ...(p.preacher && !options.some((x) => x.personId === p.preacher!.personId) ? [{ value: p.preacher.personId, label: p.preacher.name }] : []),
            ...options.map((x) => ({ value: x.personId, label: x.personId === me.personId ? `${`${x.givenName} ${x.familyName}`.trim()} (yo)` : `${x.givenName} ${x.familyName}`.trim() })),
          ]}
        />
      </div>
      <Field label="Tema" name="theme" maxLength={200} defaultValue={p.theme ?? ''} placeholder="Ej. La comunión con Dios" />
      <PassagePicker onPick={(ref) => void insert(ref)} busy={fetching} />
      {passages.length ? (
        <p className="plan__passages">
          {passages.map((x) => (
            <span key={x} className="chip-inline">
              {x}{' '}
              <button type="button" className="link-button" aria-label={`Quitar ${x}`} onClick={() => setPassages((l) => l.filter((y) => y !== x))}>
                ×
              </button>
            </span>
          ))}
        </p>
      ) : null}
      <div className="field">
        <label htmlFor="plan-scripture">Texto de los versículos</label>
        <textarea id="plan-scripture" ref={area} rows={8} maxLength={60000} value={scripture} onChange={(e) => setScripture(e.target.value)} placeholder="Inserta los pasajes arriba; aquí queda su texto." />
      </div>
      <div className="field">
        <label htmlFor="plan-notes">Notas del pastor (las ven Multimedia y la iglesia)</label>
        <textarea id="plan-notes" name="pastorNotes" rows={3} maxLength={4000} defaultValue={p.pastorNotes ?? ''} />
      </div>
      <div className="actions">
        <Button type="submit" busy={busy}>Guardar predicación</Button>
        <Button variant="ghost" onClick={() => onDone(false)}>Cancelar</Button>
      </div>
    </form>
  );
}

/** «⋯ Más» (solo la administración): editar, marcar realizado o cancelar el culto. */
function MoreActions({ churchId, service: s, manage, onChange }: { churchId: string; service: WorshipService; manage: boolean; onChange: () => void }) {
  const [mode, setMode] = useState<'closed' | 'open' | 'cancel' | 'edit'>('closed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = new Date(s.startsAt) <= new Date();
  const base = `/worship/churches/${churchId}/services/${s.id}`;
  if (!manage) return null;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setMode('closed');
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onCancel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reason = String(new FormData(event.currentTarget).get('reason'));
    void run(() => api.post(`${base}/cancel`, { reason }));
  }

  function onEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const startsAt = String(f.get('startsAt') ?? '');
    const endsAt = String(f.get('endsAt') ?? '');
    if (!startsAt || !endsAt) return setError('Pon cuándo empieza y cuándo termina.');
    if (new Date(endsAt) <= new Date(startsAt)) return setError('El culto debe terminar después de empezar.');
    void run(() => api.put(base, { title: t('title') ?? '', startsAt: fromLocalInput(startsAt), endsAt: fromLocalInput(endsAt), location: t('location'), notes: t('notes') }));
  }

  if (mode === 'closed') {
    return (
      <Button variant="ghost" aria-expanded={false} onClick={() => setMode('open')}>
        ⋯ Más
      </Button>
    );
  }
  return (
    <div className="more-actions">
      {mode === 'open' ? (
        <div className="actions actions--wrap">
          <Button variant="secondary" onClick={() => setMode('edit')}>
            Editar culto
          </Button>
          {started ? (
            <Button variant="secondary" busy={busy} onClick={() => void run(() => api.post(`${base}/held`))}>
              Marcar realizado
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => setMode('cancel')}>
            Cancelar culto
          </Button>
          <Button variant="ghost" onClick={() => setMode('closed')}>
            Cerrar
          </Button>
        </div>
      ) : mode === 'edit' ? (
        <form className="form" onSubmit={onEdit}>
          <Field label="Nombre del culto" name="title" required minLength={2} maxLength={150} defaultValue={s.title} />
          <div className="form__row">
            <Field label="Empieza" name="startsAt" type="datetime-local" required defaultValue={toLocalInput(new Date(s.startsAt))} />
            <Field label="Termina" name="endsAt" type="datetime-local" required defaultValue={toLocalInput(new Date(s.endsAt))} />
          </div>
          <Field label="Lugar" name="location" maxLength={200} defaultValue={s.location ?? ''} />
          <div className="field">
            <label htmlFor="service-notes">Notas</label>
            <textarea id="service-notes" name="notes" rows={3} maxLength={2000} defaultValue={s.notes ?? ''} />
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar cambios
            </Button>
            <Button type="button" variant="ghost" onClick={() => setMode('open')}>
              Volver
            </Button>
          </div>
        </form>
      ) : (
        <form className="form form--inline" onSubmit={onCancel} noValidate>
          <Field label="Motivo de la cancelación" name="reason" required minLength={3} maxLength={500} />
          <Button type="submit" variant="danger" busy={busy}>
            Confirmar cancelación
          </Button>
          <Button type="button" variant="ghost" onClick={() => setMode('open')}>
            Volver
          </Button>
        </form>
      )}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

const MATERIAL_ICON = { image: '🖼️', audio: '🎵', video: '🎬', document: '📄' } as const;

/**
 * Material de apoyo de la Predicación: imágenes, PDF, audios y videos en el orden en que se usarán.
 * Se guardan en Multimedia (carpeta del culto) y en Presentaciones se traen junto con los versículos.
 */
function MaterialsCard({ churchId, serviceId, materials, canEdit, onChange }: { churchId: string; serviceId: string; materials: Material[]; canEdit: boolean; onChange: () => void }) {
  const base = `/worship/churches/${churchId}/services/${serviceId}/materials`;
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setMsg(null);
    let done = 0;
    try {
      for (const file of Array.from(files)) {
        setBusy(`Subiendo ${file.name}…`);
        await api.upload(base, file);
        done += 1;
      }
      setMsg({ tone: 'success', text: done === 1 ? 'Archivo agregado. También quedó en Multimedia.' : `${done} archivos agregados. También quedaron en Multimedia.` });
    } catch (e) {
      setMsg({ tone: 'error', text: `${done ? `Se subieron ${done}. ` : ''}${errorMessage(e)}` });
    } finally {
      setBusy(null);
      if (input.current) input.current.value = '';
      onChange();
    }
  }

  async function save(ids: string[]) {
    setBusy('Guardando…');
    setMsg(null);
    try {
      await api.put(base, { resourceIds: ids });
      onChange();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }
  const ids = materials.map((m) => m.id);
  const move = (i: number, by: number) => {
    const next = [...ids];
    [next[i], next[i + by]] = [next[i + by]!, next[i]!];
    void save(next);
  };

  if (!canEdit && materials.length === 0) return null;
  return (
    <Card title="Material de apoyo">
      <p className="small muted">Imágenes, PDF, audios y videos, en el orden en que se usarán. Quedan en Multimedia y se proyectan desde Proyectar junto con los versículos (un PDF, página por página).</p>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {materials.length ? (
        <ol className="list">
          {materials.map((m, i) => (
            <li key={m.id} className="list__item list__item--actions">
              <a href={`/api${base}/${m.id}/file`} target="_blank" rel="noopener noreferrer">
                <span aria-hidden="true">{MATERIAL_ICON[m.kind]} </span>
                {m.name}
                {m.pages ? <span className="small muted"> · {m.pages} {m.pages === 1 ? 'página' : 'páginas'}</span> : null}
              </a>
              {canEdit ? (
                <span className="actions">
                  <Button variant="ghost" disabled={i === 0 || busy !== null} onClick={() => move(i, -1)} aria-label={`Subir ${m.name}`}>
                    ↑
                  </Button>
                  <Button variant="ghost" disabled={i === materials.length - 1 || busy !== null} onClick={() => move(i, 1)} aria-label={`Bajar ${m.name}`}>
                    ↓
                  </Button>
                  <Button variant="ghost" disabled={busy !== null} onClick={() => void save(ids.filter((x) => x !== m.id))} aria-label={`Quitar ${m.name}`}>
                    Quitar
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">Todavía no hay material.</p>
      )}
      {canEdit ? (
        <div className="actions actions--wrap">
          <label className={`btn btn--secondary${busy ? ' btn--disabled' : ''}`}>
            {busy ?? '+ Subir archivos'}
            <input
              ref={input}
              type="file"
              multiple
              className="visually-hidden"
              accept="image/jpeg,image/png,image/gif,image/webp,audio/*,video/mp4,video/webm,application/pdf"
              disabled={busy !== null}
              aria-label="Subir material de apoyo"
              onChange={(e) => void upload(e.target.files)}
            />
          </label>
          <span className="small muted">¿Tienes un Word o PowerPoint? Guárdalo como PDF y súbelo.</span>
        </div>
      ) : null}
    </Card>
  );
}
